import { Component, Input, OnChanges } from '@angular/core';

import { DisplayType, ParticipantOption, ParticipantPriceType, Price } from '@cds/betting-offer';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { INativePrice } from '@frontend/sports/odds/feature/native-price';
import { HorseParticipantInfo, WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { ParticipantPickType } from 'packages/sports/common/betslip/core/picks/pick-models';

import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { GreyhoundTrackingService } from '../../common/greyhound-tracking.service';

interface TimeformTipViewModel {
    fixture: WinGameFixture;
    participant: WinGameParticipant;
    option?: ParticipantOption;
    isOptionActive: boolean;
    hasAnalyst: boolean;
    priceHistory: Price[];
    participantInfo: HorseParticipantInfo;
    expandedTimeformTip: boolean;
    eventOption?: EventOption | undefined;
}

@Component({
    selector: 'ms-timeform-tip',
    templateUrl: 'timeform-tip.component.html',
    standalone: false,
})
export class TimeformTipComponent implements OnChanges {
    @Input() participant: WinGameParticipant;
    @Input() fixture: WinGameFixture;
    @Input() participantOption?: ParticipantOption;
    @Input() trackingModuleType: string;

    participantOptionGroups: { [id: string]: EventOption[] } = {};
    isOptionBet: boolean;
    vm?: TimeformTipViewModel;
    startingPriceType = ParticipantPriceType.StartingPrice;
    nativePrice: INativePrice | undefined;
    trackingOptions: { [key: string]: string };
    pickType = ParticipantPickType.Win;
    trackingSource = 'horseracing_timeform_tip';
    selectedOptionGroup: EventOptionGroup | undefined;

    constructor(
        public siteCore: Sitecore,
        private pricingHelper: HorsesFixturePricingHelper,
        private participantHelper: HorsesFixtureParticipantHelper,
        private greyhoundTrackingService: GreyhoundTrackingService,
    ) {}

    ngOnChanges(changes: ISimpleChanges<TimeformTipComponent>): void {
        if (this.fixture && this.participant) {
            const collapseTimeFormTip = !!(
                changes.participant &&
                (changes.participant.firstChange || changes.participant.previousValue.id !== changes.participant.currentValue.id)
            );
            this.isOptionBet = this.fixture.isOptionBet;
            this.buildViewModel(collapseTimeFormTip);
        } else {
            this.vm = undefined;
        }
    }

    private buildViewModel(collapseTimeFormTip: boolean): void {
        const hasAnalyst = !!(this.fixture.timeform?.analystVerdict && this.fixture.timeform.analystVerdict.trim());
        this.selectedOptionGroup = this.fixture.optionGroups.find((t) => t.detailedGrouping.displayType === DisplayType.Wincast);
        this.participantOptionGroups = this.participantHelper.buildWinParticipantOptionGroups(this.fixture);
        const priceType =
            this.isOptionBet && this.selectedOptionGroup && this.selectedOptionGroup.isWinPriceAvailable
                ? ParticipantPriceType.Fixed
                : ParticipantPriceType.StartingPrice;
        this.vm = {
            expandedTimeformTip: hasAnalyst && !collapseTimeFormTip && this.vm && this.vm.expandedTimeformTip ? this.vm.expandedTimeformTip : false, // keep previous or collapse it.
            option: this.participantOption,
            participant: this.participant,
            fixture: this.fixture,
            isOptionActive: this.pricingHelper.isAvailableWinOption(this.participantOption),
            hasAnalyst,
            priceHistory: this.pricingHelper.getPriceHistory(this.participantOption),
            participantInfo: this.participantHelper.getHorseRaceInfo(this.participant),
            eventOption: this.pricingHelper.getParticipantOptions(this.participant.id, this.participantOptionGroups, priceType),
        };
        this.nativePrice = this.fixture.isOptionBet ? this.vm?.eventOption?.nativePrice.toJSON() : undefined;
        this.trackingOptions = this.greyhoundTrackingService.getAdditionalTrackingOptions(this.fixture, this.trackingSource, true);
    }

    toggleTimeformTip(): void {
        if (this.vm) {
            this.vm.expandedTimeformTip = !this.vm.expandedTimeformTip;
            this.greyhoundTrackingService.trackExpandCollapse(this.vm.expandedTimeformTip, this.fixture, this.vm.participant, true);
        }
    }
}
